/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type MouseEvent, type FC, useRef } from 'react';

import {
  type IOnImageClickParams,
  type IOnLinkClickParams,
  type IBaseContentProps,
  type MdBoxProps,
} from '@coze-common/chat-uikit-shared';
import { Image } from '@coze-arch/bot-md-box-adapter/slots';
import { type ImageOptions } from '@coze-arch/bot-md-box-adapter';

import { CozeLink } from '../../md-box-slots/link';
import { CozeImage } from '../../md-box-slots/coze-image';
import { LazyCozeMdBox } from '../../common/coze-md-box/lazy';
import { isText } from '../../../utils/is-text';
import './index.less';

/**
 * 【2026-11-01 修复】正文「裸 JSON 粘成一坨」不可读。
 *
 * 背景：模型有时把工具返回的结构化数据（如 `[{"index":1,"feature":"…"},…]`）
 * 直接当正文吐出来。calypso（markdown 解析器）把它当**普通段落**处理，
 * CommonMark 规定段落内的软换行折叠成空格 → 整坨连成一行，没有缩进、
 * 没有高亮、不能折叠，用户完全没法看。
 *
 * 修法：仅当「整段正文 trim 后就是一段完整 JSON」时，重新格式化成
 * ```json 围栏代码块。理由：
 *   1. 不改变任何语义/内容，只加围栏 + 缩进，属于纯展示层兜底；
 *   2. 围栏代码块走 calypso 的既有能力（等宽、高亮、长内容折叠），
 *      不引入新依赖，不做业务猜测（不猜字段含义、不擅自转表格）；
 *   3. 只在消息 is_finish 之后处理，流式过程中保持原文，
 *      避免半截 JSON 被强行 fence 后闪烁跳动。
 *
 * 边界：正文不是整体 JSON（如 markdown 报告里夹带 JSON 片段）时原样返回，
 * 一个字符都不动。JSON.parse 失败同样原样返回。
 */
const wrapPureJsonBlock = (raw: string): string => {
  const trimmed = raw.trim();
  // 太短的不可能是「一坨 JSON」，直接放行，省掉无谓的 parse 开销
  if (trimmed.length < 2) {
    return raw;
  }
  // 已经是围栏代码块（模型自己包了），不要重复包
  if (trimmed.startsWith('```')) {
    return raw;
  }
  const firstChar = trimmed.charAt(0);
  const lastChar = trimmed.charAt(trimmed.length - 1);
  const isJsonShaped =
    (firstChar === '{' && lastChar === '}') ||
    (firstChar === '[' && lastChar === ']');
  if (!isJsonShaped) {
    return raw;
  }
  // 快速排除：JSON 必然含 `"key":`，不含就不像 JSON，别做 parse
  if (!/"[^"]*"\s*:/.test(trimmed)) {
    return raw;
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return ['```json', JSON.stringify(parsed, null, 2), '```'].join('\n');
  } catch {
    return raw;
  }
};

export type IMessageContentProps = IBaseContentProps & {
  onImageClick?: (params: IOnImageClickParams) => void;
  mdBoxProps?: MdBoxProps;
  enableAutoSizeImage?: boolean;
  imageOptions?: ImageOptions;
  onLinkClick?: (
    params: IOnLinkClickParams,
    event: MouseEvent<Element, globalThis.MouseEvent>,
  ) => void;
};

export const TextContent: FC<IMessageContentProps> = props => {
  const {
    message,
    readonly,
    onImageClick,
    onLinkClick,
    mdBoxProps,
    enableAutoSizeImage,
    imageOptions,
  } = props;
  const MdBoxLazy = LazyCozeMdBox;
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { content } = message;

  if (!isText(content)) {
    return null;
  }

  const isStreaming = !message.is_finish;
  const rawText = content.slice(0, message.broken_pos ?? Infinity);
  // 只在「本轮已生成完毕」时格式化裸 JSON；流式中间态保持原文，
  // 否则半截 JSON 会被反复 fence/解 fence，屏幕会闪烁跳动。
  const text = isStreaming ? rawText : wrapPureJsonBlock(rawText);
  return (
    <div
      className="chat-uikit-text-content"
      data-testid="bot.ide.chat_area.message.text-answer-message-content"
      ref={contentRef}
      data-grab-mark={message.message_id}
      data-grab-source={message.source}
    >
      <MdBoxLazy
        markDown={text}
        autoFixSyntax={{ autoFixEnding: isStreaming }}
        showIndicator={isStreaming}
        smooth={isStreaming}
        imageOptions={{ forceHttps: !IS_OPEN_SOURCE, ...imageOptions }}
        eventCallbacks={{
          onImageClick: (e, eventData) => {
            eventData.src &&
              onImageClick?.({
                message,
                extra: { url: eventData.src },
              });
          },
          onLinkClick: (e, eventData) => {
            onLinkClick?.(
              {
                message,
                extra: { ...eventData },
              },
              e,
            );

            if (readonly) {
              e.preventDefault();
              e.stopPropagation();
            }
          },
        }}
        {...mdBoxProps}
        slots={{
          Image: enableAutoSizeImage ? CozeImage : Image,
          Link: CozeLink,
          ...mdBoxProps?.slots,
        }}
      ></MdBoxLazy>
    </div>
  );
};

TextContent.displayName = 'TextContent';
